// Screen 1: Dashboard / Research Briefing — "mission control" for the morning.
// Layout: sidebar | (topbar + main grid + right insight rail)

import { usePapers } from '../src/lib/use-papers.js';

function DashboardScreen() {
  const { t } = useT();
  const { papers } = usePapers();
  return (
    <div className="pp pp-screen" data-screen-label="01 Dashboard">
      <Sidebar active="dashboard"/>

      <div className="pp-main">
        <TopBar breadcrumbs={<Breadcrumb items={[t('crumb.workspace'), t('crumb.dashboard')]}/>}/>

        <div className="pp-content">
          {/* center workspace */}
          <div className="pp-dashboard-center scroll">
            {/* greeting */}
            <div className="pp-greeting">
              <div className="col gap-2" style={{ minWidth: 0, flex: 1 }}>
                <div className="row gap-3" style={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <span className="serif nowrap" style={{ fontSize: 28, lineHeight: 1.05, letterSpacing: '-0.015em' }}>
                    {t('dash.greeting')}
                  </span>
                  <span style={{ fontSize: 11.5, color: 'var(--fg-3)', letterSpacing: '0.08em' }} className="mono">SAT · 17 MAY · 09:42 KST</span>
                </div>
                <div style={{ fontSize: 13.5, color: 'var(--fg-1)', maxWidth: 720 }}>
                  {t('dash.greeting.sub', 128, 14, 3)}
                </div>
              </div>
              <div className="pp-greeting-actions">
                <button className="btn ghost sm"><I name="clock" size={13}/> {t('dash.yesterday')}</button>
                <button className="btn sm"><I name="filter" size={13}/> {t('dash.filters')}</button>
                <button className="btn primary sm"><I name="sparkles" size={13}/> {t('dash.generate')}</button>
              </div>
            </div>

            {/* KPI strip */}
            <div className="pp-kpi-strip">
              <KPI label={t('kpi.new')}      value="128" delta={'+18 ' + t('kpi.delta.vs')} deltaKind="up"   spark={[40,52,38,55,71,68,82,128]} icon="data"/>
              <KPI label={t('kpi.unread')}   value="14"  delta="+3"            deltaKind="up"   spark={[10,9,11,8,12,11,12,14]}    icon="flame"   accent="var(--acc-violet)"/>
              <KPI label={t('kpi.bench')}    value="6"   delta="+2"            deltaKind="up"   spark={[2,1,3,2,4,3,5,6]}          icon="beaker"  accent="var(--acc-cyan)"/>
              <KPI label={t('kpi.citation')} value="11"  delta="−4"            deltaKind="down" spark={[18,16,14,12,10,12,13,11]}  icon="link"/>
              <KPI label={t('kpi.code')}     value="9"   delta="+3"            deltaKind="up"   spark={[4,3,5,6,5,7,6,9]}          icon="github" accent="var(--ok)"/>
              <KPI label={t('kpi.project')}  value="14"  delta="+5"            deltaKind="up"   spark={[6,5,8,7,9,10,12,14]}       icon="kanban" accent="var(--acc-violet)"/>
            </div>

            {/* must-read queue */}
            <section className="pp-block">
              <SectionHead
                kicker={t('dash.s1.kicker')}
                title={t('dash.s1.title')}
                sub={t('dash.s1.sub')}
                action={
                  <div className="row gap-2">
                    <span className="chip ghost mono"><span className="dot" style={{ background: 'var(--acc-violet)' }}/> {t('dash.s1.count')}</span>
                    <button className="btn sm ghost">{t('dash.seeall')} <I name="chevronRight" size={12}/></button>
                  </div>
                }
              />
              <div className="pp-must-grid">
                {papers.slice(0, 2).map((p) => <PaperCard key={p.id} paper={p}/>)}
              </div>
            </section>

            {/* recommended */}
            <section className="pp-block">
              <SectionHead
                kicker={t('dash.s2.kicker')}
                title={t('dash.s2.title')}
                sub={t('dash.s2.sub')}
                action={<RecTabs/>}
              />

              {/* Dense recommendation list */}
              <div className="pp-rec-list">
                {papers.slice(2, 5).map(p => (
                  <RecRow key={p.id} paper={p}/>
                ))}
              </div>
            </section>

            {/* split: trending + code released */}
            <section className="pp-split">
              <div className="pp-block card" style={{ padding: 16 }}>
                <SectionHead
                  kicker={t('dash.s3.kicker')}
                  title={t('dash.s3.title')}
                  action={<span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-3)' }}>{t('dash.s3.unit')}</span>}
                />
                <TopicTrend/>
              </div>

              <div className="pp-block card" style={{ padding: 16 }}>
                <SectionHead
                  kicker={t('dash.s4.kicker')}
                  title={t('dash.s4.title')}
                  action={<span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-3)' }}>{t('dash.s4.unit')}</span>}
                />
                <div className="col gap-3">
                  {[
                    { p: papers[0], stars: '412', lang: 'Python · CUDA' },
                    { p: papers[1], stars: '2.1k', lang: 'Python · JAX' },
                    { p: papers[5] ?? papers[2], stars: '186', lang: 'Python' },
                  ].filter((x) => x.p).map((x) => (
                    <CodeRow key={x.p.id} paper={x.p} stars={x.stars} lang={x.lang}/>
                  ))}
                </div>
              </div>
            </section>
          </div>

          {/* RIGHT INSIGHT RAIL */}
          <RailRight id="dashboard-rail" defaultWidth={340} min={280} max={480} className="pp-rail scroll">
            <RailSection title={t('rail.resume.title')} kicker={t('rail.resume.kicker')}>
              <div className="pp-resume">
                <div className="row gap-3" style={{ alignItems: 'flex-start' }}>
                  <div className="pp-mini-pdf"/>
                  <div className="col grow gap-1" style={{ minWidth: 0 }}>
                    <div className="serif ellipsis" style={{ fontSize: 14, color: 'var(--fg-0)' }}>OpenVLA: An Open-Source Vision-Language-Action Model</div>
                    <div style={{ fontSize: 11, color: 'var(--fg-3)' }}>{t('rail.resume.where')}</div>
                    <div className="pp-progress" style={{ marginTop: 6 }}>
                      <div className="pp-progress-bar" style={{ width: '32%' }}/>
                    </div>
                  </div>
                </div>
              </div>
            </RailSection>

            <RailSection title={t('rail.follow.title')} kicker={t('rail.follow.kicker')} badge="4">
              <FollowupRow title={t('rail.follow.r1.title')} venue="CVPR 24" reason={t('rail.follow.r1.reason')} />
              <FollowupRow title={t('rail.follow.r2.title')} venue="RSS 25" reason={t('rail.follow.r2.reason')} />
              <FollowupRow title={t('rail.follow.r3.title')} venue="arXiv" reason={t('rail.follow.r3.reason')} />
            </RailSection>

            <RailSection title={t('rail.pulse.title')} kicker={t('rail.pulse.kicker')}>
              <WeeklyPulse/>
            </RailSection>

            <RailSection title={t('rail.ask.title')} kicker={t('rail.ask.kicker')}>
              <div className="pp-ask-card">
                <div style={{ fontSize: 12, color: 'var(--fg-2)', marginBottom: 8 }}>{t('rail.ask.suggest')}</div>
                <div className="col gap-2">
                  <SuggestedQ q={t('rail.ask.q1')}/>
                  <SuggestedQ q={t('rail.ask.q2')}/>
                  <SuggestedQ q={t('rail.ask.q3')}/>
                </div>
                <div className="pp-ask-input">
                  <I name="sparkles" size={13} style={{ color: 'var(--acc-indigo)' }}/>
                  <span style={{ flex: 1, color: 'var(--fg-3)', fontSize: 12 }}>{t('rail.ask.input')}</span>
                  <span className="pp-kbd">⌘J</span>
                </div>
              </div>
            </RailSection>
          </RailRight>
        </div>
      </div>
    </div>
  );
}

function RecTabs() {
  const [active, setActive] = React.useState('All');
  const items = [{ k: 'All', c: '17' }, { k: '4DGS', c: '6' }, { k: 'VLA', c: '4' }, { k: 'SfM', c: '2' }];
  return (
    <div className="row gap-2">
      <div className="pp-tabs">
        {items.map(({ k, c }) => (
          <button key={k} className={'pp-tab ' + (active === k ? 'is-active' : '')} onClick={() => setActive(k)}>
            {k} <span className="mono" style={{ color: 'var(--fg-3)' }}>{c}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// Dense recommendation row — flatter than the must-read card
function RecRow({ paper }) {
  const { t } = useT();
  const nav = React.useContext(NavContext);
  return (
    <div className="pp-rec-row" onClick={() => nav.navigate && nav.navigate('detail', paper?.id ? { id: paper.id } : undefined)}>
      <div className="col gap-1 grow" style={{ minWidth: 0 }}>
        <div className="row gap-2" style={{ alignItems: 'baseline' }}>
          <span className="chip accent">{paper.venue}</span>
          <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-3)' }}>{paper.date}</span>
          {paper.tags?.slice(0, 2).map(t => <span key={t} className="tag indigo">{t}</span>)}
          {paper.code === 'released' && <span className="chip ok"><I name="github" size={10}/> code</span>}
        </div>
        <div className="serif ellipsis" style={{ fontSize: 15, color: 'var(--fg-0)', marginTop: 2 }}>{paper.title}</div>
        <div className="ellipsis" style={{ fontSize: 11.5, color: 'var(--fg-2)' }}>
          {paper.authors.slice(0, 3).join(', ')} · {paper.summary?.slice(0, 96)}…
        </div>
        {paper.why && (
          <div className="row gap-2" style={{ marginTop: 4 }}>
            <I name="sparkles" size={10} style={{ color: 'var(--acc-violet)' }}/>
            <span style={{ fontSize: 11, color: 'var(--acc-violet)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('pc.why')}</span>
            <span style={{ fontSize: 11.5, color: 'var(--fg-1)' }} className="ellipsis">{paper.why}</span>
          </div>
        )}
      </div>
      <div className="row gap-3" style={{ marginLeft: 12, flexShrink: 0 }}>
        <ScoreInline label={t('pc.score.worth')} value={paper.worth} color="var(--acc-indigo)"/>
        <ScoreInline label={t('pc.score.novel')} value={paper.novel} color="var(--acc-violet)"/>
        <ScoreInline label={t('pc.score.impl')}  value={paper.impl}  color="var(--acc-cyan)"/>
      </div>
      <div className="row gap-1" style={{ marginLeft: 8 }} onClick={e => e.stopPropagation()}>
        <button className="iconbtn"><I name="bookmark" size={14}/></button>
        <button className="iconbtn" onClick={() => nav.navigate && nav.navigate('detail', paper?.id ? { id: paper.id } : undefined)}><I name="chevronRight" size={14}/></button>
      </div>
    </div>
  );
}

// Trending topics chart — vertical bar grid
function TopicTrend() {
  const topics = [
    { name: '4DGS',                 vals: [4,6,5,9,12,15,18], change: '+62%', color: 'var(--acc-indigo)' },
    { name: 'VLA · real robot',     vals: [3,4,5,4,7,10,14],  change: '+48%', color: 'var(--acc-violet)' },
    { name: 'sparse-view 3DGS',     vals: [2,3,2,5,6,7,9],    change: '+34%', color: 'var(--acc-cyan)' },
    { name: 'robot foundation',     vals: [5,4,6,5,7,7,8],    change: '+12%', color: 'var(--ok)' },
    { name: 'NeRF compression',     vals: [6,5,5,4,3,3,3],    change: '−42%', color: 'var(--warn)' },
  ];
  return (
    <div className="col gap-2" style={{ marginTop: 4 }}>
      {topics.map(t => {
        const max = Math.max(...t.vals);
        const isNeg = t.change.startsWith('−');
        return (
          <div key={t.name} className="row gap-3" style={{ alignItems: 'center' }}>
            <div style={{ width: 130, fontSize: 12, color: 'var(--fg-1)' }} className="ellipsis">{t.name}</div>
            <div className="row gap-1" style={{ flex: 1, alignItems: 'flex-end', height: 28 }}>
              {t.vals.map((v, i) => (
                <div key={i} style={{
                  flex: 1, height: `${(v / max) * 100}%`, minHeight: 2,
                  background: `linear-gradient(180deg, ${t.color}, ${t.color}55)`,
                  borderRadius: 2, opacity: 0.4 + (i / t.vals.length) * 0.6,
                }}/>
              ))}
            </div>
            <div className="mono" style={{ width: 48, textAlign: 'right', fontSize: 11, color: isNeg ? 'var(--warn)' : 'var(--ok)' }}>{t.change}</div>
          </div>
        );
      })}
    </div>
  );
}

function CodeRow({ paper, stars, lang }) {
  const nav = React.useContext(NavContext);
  return (
    <div className="pp-code-row" onClick={() => nav.navigate && nav.navigate('detail', paper?.id ? { id: paper.id } : undefined)}>
      <div className="pp-code-icon"><I name="github" size={14}/></div>
      <div className="col grow gap-1" style={{ minWidth: 0 }}>
        <div className="serif ellipsis" style={{ fontSize: 13, color: 'var(--fg-0)' }}>{paper.title}</div>
        <div className="row gap-3 mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>
          <span>{lang}</span>
          <span>★ {stars}</span>
          <span>2h ago</span>
        </div>
      </div>
      <button className="btn sm ghost" onClick={e => { e.stopPropagation(); }}><I name="external" size={12}/></button>
    </div>
  );
}

function RailSection({ title, kicker, badge, children }) {
  return (
    <div className="pp-rail-section">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
        <div className="col gap-1">
          <div className="section-title" style={{ fontSize: 10, letterSpacing: '0.14em' }}>
            <span className="num">{kicker}</span>
          </div>
          <div style={{ fontSize: 13, color: 'var(--fg-0)', fontWeight: 500 }}>{title}</div>
        </div>
        {badge && <span className="chip ghost mono">{badge}</span>}
      </div>
      {children}
    </div>
  );
}

function FollowupRow({ title, venue, reason }) {
  return (
    <div className="row gap-2 pp-follow" style={{ padding: '8px 0', borderBottom: '1px solid var(--border-0)' }}>
      <div className="pp-follow-tick"/>
      <div className="col grow gap-1" style={{ minWidth: 0 }}>
        <div className="ellipsis" style={{ fontSize: 12, color: 'var(--fg-0)' }}>{title}</div>
        <div className="row gap-2" style={{ fontSize: 10.5, color: 'var(--fg-3)' }}>
          <span className="mono">{venue}</span>
          <span>·</span>
          <span style={{ color: 'var(--acc-cyan)' }}>{reason}</span>
        </div>
      </div>
    </div>
  );
}

function WeeklyPulse() {
  const { t } = useT();
  const data = [12,14,11,18,16,22,28];
  const days = ['M','T','W','T','F','S','S'];
  const max = Math.max(...data);
  return (
    <div className="col gap-2">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div className="serif" style={{ fontSize: 22 }}>121<span style={{ fontSize: 12, color: 'var(--fg-3)', marginLeft: 4 }}>{t('rail.pulse.unit')}</span></div>
        <span className="chip ok mono">+34%</span>
      </div>
      <div className="row" style={{ height: 56, alignItems: 'flex-end', gap: 4 }}>
        {data.map((v, i) => (
          <div key={i} className="col gap-1" style={{ flex: 1, alignItems: 'center' }}>
            <div style={{
              width: '100%', height: `${(v/max)*100}%`,
              background: `linear-gradient(180deg, var(--acc-indigo), rgba(136,150,255,0.2))`,
              borderRadius: 2,
              boxShadow: i === data.length - 1 ? '0 0 8px var(--acc-indigo)' : 'none',
            }}/>
            <span className="mono" style={{ fontSize: 9.5, color: 'var(--fg-3)' }}>{days[i]}</span>
          </div>
        ))}
      </div>
      <div className="row gap-2" style={{ fontSize: 10.5, color: 'var(--fg-2)', marginTop: 4 }}>
        <span style={{ flex: 1 }}>{t('rail.pulse.note')}</span>
      </div>
    </div>
  );
}

function SuggestedQ({ q }) {
  const nav = React.useContext(NavContext);
  return (
    <button className="pp-suggested-q" onClick={() => nav.navigate && nav.navigate('assistant')}>
      <I name="chevronRight" size={11} style={{ color: 'var(--fg-3)' }}/>
      <span className="ellipsis">{q}</span>
    </button>
  );
}

// styles
if (!document.getElementById('pp-dashboard-styles')) {
  const s = document.createElement('style');
  s.id = 'pp-dashboard-styles';
  s.textContent = `
  .pp-dashboard-center {
    flex: 1; min-width: 0;
    padding: 22px 26px 32px;
    overflow: auto;
    display: flex; flex-direction: column; gap: 26px;
  }
  .pp-rail {
    width: 340px; flex-shrink: 0;
    border-left: 1px solid var(--border-1);
    background: var(--bg-1);
    padding: 22px 18px 28px;
    overflow: auto;
    display: flex; flex-direction: column; gap: 26px;
  }
  .pp-rail-section { display: flex; flex-direction: column; }

  .pp-greeting {
    display: flex; align-items: center; justify-content: space-between; gap: 24px;
    position: relative;
  }
  .pp-greeting-actions { display: flex; gap: 6px; align-items: center; flex-shrink: 0; }

  .pp-kpi-strip {
    display: grid; grid-template-columns: repeat(6, 1fr); gap: 10px;
  }

  .pp-block { display: flex; flex-direction: column; }
  .pp-must-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }

  .pp-tabs { display: flex; gap: 2px; padding: 3px; background: var(--bg-2); border: 1px solid var(--border-1); border-radius: 8px; }
  .pp-tab { padding: 4px 10px; border-radius: 5px; font-size: 11.5px; color: var(--fg-2); display: flex; align-items: center; gap: 6px; }
  .pp-tab:hover { color: var(--fg-0); }
  .pp-tab.is-active { background: var(--bg-4); color: var(--fg-0); box-shadow: 0 1px 0 rgba(255,255,255,0.05) inset; }
  .pp-tab .mono { font-family: var(--font-mono); font-size: 10px; }

  .pp-rec-list { display: flex; flex-direction: column; }
  .pp-rec-row {
    display: flex; align-items: center;
    padding: 14px 6px; gap: 8px;
    border-bottom: 1px solid var(--border-1);
    cursor: pointer;
  }
  .pp-rec-row:last-child { border-bottom: 0; }
  .pp-rec-row:hover { background: rgba(255,255,255,0.015); }

  .pp-split { display: grid; grid-template-columns: 1.2fr 1fr; gap: 14px; }

  .pp-resume {
    background: var(--bg-2); border: 1px solid var(--border-1); border-radius: 10px; padding: 12px;
  }
  .pp-mini-pdf {
    width: 44px; height: 56px; flex-shrink: 0;
    background: linear-gradient(180deg, #2a2e3e, #1a1d28);
    border: 1px solid var(--border-2);
    border-radius: 3px;
    position: relative;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  }
  .pp-mini-pdf::before, .pp-mini-pdf::after {
    content: ''; position: absolute; left: 6px; right: 6px;
    height: 1.5px; background: rgba(255,255,255,0.10); border-radius: 1px;
  }
  .pp-mini-pdf::before { top: 10px; box-shadow: 0 4px 0 rgba(255,255,255,0.10), 0 8px 0 rgba(255,255,255,0.10), 0 12px 0 rgba(136,150,255,0.20), 0 16px 0 rgba(255,255,255,0.10), 0 20px 0 rgba(255,255,255,0.10); }
  .pp-mini-pdf::after { bottom: 8px; width: 24px; background: rgba(136,150,255,0.30); }
  .pp-progress { height: 3px; background: rgba(255,255,255,0.06); border-radius: 999px; overflow: hidden; }
  .pp-progress-bar { height: 100%; background: linear-gradient(90deg, var(--acc-indigo), var(--acc-violet)); box-shadow: 0 0 6px var(--acc-indigo); }

  .pp-follow-tick {
    width: 6px; height: 6px; border-radius: 999px; background: var(--acc-cyan);
    margin-top: 6px; flex-shrink: 0;
    box-shadow: 0 0 6px var(--acc-cyan);
  }

  .pp-ask-card {
    background: linear-gradient(180deg, rgba(136,150,255,0.04), rgba(180,155,255,0.02) 60%, transparent);
    border: 1px solid var(--border-1);
    border-radius: 10px;
    padding: 12px;
  }
  .pp-suggested-q {
    display: flex; align-items: center; gap: 6px;
    padding: 8px 10px; border-radius: 6px;
    border: 1px solid var(--border-1);
    background: var(--bg-2);
    color: var(--fg-1); font-size: 11.5px;
    text-align: left; transition: border-color .12s;
  }
  .pp-suggested-q:hover { border-color: rgba(136,150,255,0.40); color: var(--fg-0); }
  .pp-ask-input {
    margin-top: 10px;
    display: flex; align-items: center; gap: 8px;
    padding: 8px 10px;
    border: 1px solid rgba(136,150,255,0.20);
    background: rgba(136,150,255,0.04);
    border-radius: 8px;
  }

  .pp-code-row {
    display: flex; align-items: center; gap: 10px;
    padding: 8px; border-radius: 8px;
    border: 1px solid var(--border-1);
    background: var(--bg-3);
  }
  .pp-code-icon {
    width: 32px; height: 32px; border-radius: 7px;
    background: rgba(136,150,255,0.08);
    color: var(--acc-indigo);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    border: 1px solid rgba(136,150,255,0.18);
  }
  `;
  document.head.appendChild(s);
}

export { DashboardScreen };
